import React, { lazy } from "react";
import { DataFileBriefing } from "@/views/briefing/data-file-briefing";
import { PinWorkshop } from "@/views/siege/world-map/pin-workshop";

/**
 * Dev-only tool registry (mounted under `/dev/*`). These routes are gated at
 * build time by `import.meta.env.DEV` in the router below, so the entire tree
 * — components and this registry — is tree-shaken out of production bundles.
 * That is the security model: the tools simply do not exist in prod (several
 * also depend on Vite serve-only middleware, e.g. the siege map save endpoint).
 */
const CutsceneEditor = lazy(() => import("../CutsceneEditor"));
const SiegeMapEditor = lazy(() => import("../SiegeMapEditor"));
const SiegeWorldMapSandbox = lazy(() => import("../SiegeWorldMapSandbox"));
const SiegeCampaignEditor = lazy(() => import("../SiegeCampaignEditor"));
const SkillShowcase = lazy(() => import("../SkillShowcase"));
const CardShowcase = lazy(() => import("../CardShowcase"));

export interface DevTool {
  /** Sub-path under `/dev` (no leading slash). */
  slug: string;
  label: string;
  description: string;
  render: () => React.ReactNode;
}

export const DEV_ROOT = "/dev";

export const DEV_TOOLS: DevTool[] = [
  {
    slug: "pin-workshop",
    label: "Pin Workshop",
    description: "8 visual approaches for the world-map site marker.",
    render: () => <PinWorkshop />,
  },
  {
    slug: "siege-world-map",
    label: "Siege World Map",
    description: "Visual sandbox for the pre-lobby world map + location pins.",
    render: () => <SiegeWorldMapSandbox />,
  },
  {
    slug: "siege-map-editor",
    label: "Siege Map Editor",
    description: "Author siege maps: fit art, paint collision, place zones.",
    render: () => <SiegeMapEditor />,
  },
  {
    slug: "siege-campaign-editor",
    label: "Siege Campaign Editor",
    description: "Place world-map pins and draw the campaign DAG unlock graph.",
    render: () => <SiegeCampaignEditor />,
  },
  {
    slug: "cutscene-editor",
    label: "Cutscene Editor",
    description: "Build and record cutscenes.",
    render: () => <CutsceneEditor />,
  },
  {
    slug: "briefing-preview",
    label: "Briefing Preview",
    description: "Preview the data-file briefing screen.",
    render: () => <DataFileBriefing />,
  },
  {
    slug: "skill-showcase",
    label: "Skill Showcase",
    description: "Offline VFX / telegraph lab: cast any skill on a grid.",
    render: () => <SkillShowcase />,
  },
  {
    slug: "card-showcase",
    label: "Card Showcase",
    description: "TCG card lab: species, shiny, full-art, foil, talent rank.",
    render: () => <CardShowcase />,
  },
];

export const devToolHref = (slug: string) => `${DEV_ROOT}/${slug}`;
